// ADR 7.10.26v §4 — the companion page: `ShellBody`'s index 2, the seam 7.10.26w fills.
//
// One element type at every spread, so nothing inside it remounts when the
// device opens. At `OnePage` / `Tabletop` the host is `display: contents` and
// the dock's column or sheet lays out exactly as it did before the host
// existed; at `TwoPages` it widens into the grid's trailing track. The
// `contents` is an inline style as well as the class's meaning: `useDockSize`
// walks past a `contents` ancestor by its computed `display`, and jsdom applies
// no Tailwind, so the walk is exercised under test only if the style is inline.
//
// v owns the border, the padding and the companion's own viewport-tall scroll:
// it is sticky, so it stays in view while the learner's page scrolls, as a
// facing page does. `border-s` is the companion's inline-start edge, which is
// the fold side in both directions.

import type { CSSProperties, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { PageWindowClass, useSpreadValue } from '../windowClassContext'
import { PAGED } from './readSpread'

const HOST: Record<'track' | 'loose', string> = {
  loose: 'contents',
  track:
    'sticky top-0 self-start flex max-h-[100dvh] min-w-0 flex-col overflow-y-auto px-4 py-4 ' +
    'border-s border-line',
}

const CONTENTS: CSSProperties = { display: 'contents' }

/** ShellBody's index 2: one element type at every spread; its children are 7.10.26w's `<CompanionContents/>`. */
export function CompanionPage({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const spread = useSpreadValue()
  const paged = PAGED[spread.layout]
  return (
    <PageWindowClass value={spread.companion}>
      <div
        data-testid="companion-page"
        role={paged ? 'complementary' : undefined}
        aria-label={paged ? t('spread_companion_label') : undefined}
        className={HOST[paged ? 'track' : 'loose']}
        style={paged ? undefined : CONTENTS}
      >
        {children}
      </div>
    </PageWindowClass>
  )
}
